/**
 * What the terrain painter draws, decided without a canvas: the regions in paint order with their
 * layers, the cover props a window holds (placed clear of each other and of impassable ground), and
 * each raised region's wall height. `paint.ts` turns this
 * into pixels; everything here is a pure function of the terrain and board coordinates, so a
 * chunk decides exactly what the whole board would inside it (`scene.test.ts`).
 */
import type { EdgeMap, TerrainMap } from "@lancer-vtt/domain";
import { CELL } from "../combatCells";
import { type Board, type CellRect, type Region, clusterPlan, groupRegions, maskHas, rnd, softProps, stringSeed } from "./geometry";
import { type ArtLayers, type GroundId, type MaterialId, type PropKind, DEFAULT_GROUND, artLayers, tileArtFor } from "./tileArt";

/** `grid: false` paints no corner crosses (a tile swatch); the board always has them. */
export type TerrainScene = { board: Board; terrain: TerrainMap; edges: EdgeMap; ground?: GroundId; grid?: boolean };

export type SceneRegion = Region & { layers: ArtLayers; seed: number };

export type PreparedScene = { board: Board; edges: EdgeMap; ground: GroundId; grid: boolean; regions: SceneRegion[] };

/**
 * Surfaces paint calm first, then rough, then liquids, so a crisp or opaque edge lands over a
 * feathered one where two regions meet. Ties break on the region key.
 */
function surfaceOrder(r: SceneRegion): number {
  const s = r.layers.surface;
  if (!s) return 3;
  if (s.region.feather < 0.05) return 2;
  return s.rough ? 1 : 0;
}

export function prepareScene(scene: TerrainScene): PreparedScene {
  const regions = groupRegions(scene.terrain, scene.board).map((r) => ({ ...r, layers: artLayers(tileArtFor(r.cell), r.properties), seed: stringSeed(r.key) }));
  regions.sort((a, b) => surfaceOrder(a) - surfaceOrder(b) || (a.key < b.key ? -1 : 1));
  return { board: scene.board, edges: scene.edges, ground: scene.ground ?? DEFAULT_GROUND, grid: scene.grid ?? true, regions };
}

/** One cover prop to draw: what it depicts, where, how big, and which shadow its grade casts. */
export type PropDraw = {
  kind: PropKind;
  material: MaterialId;
  shadow: "short" | "long";
  /** The hard-cover cluster shape it stands for (`single` for a soft prop). */
  cluster: "slab" | "pair" | "single";
  cx: number;
  cy: number;
  r: number;
  rot: number;
  seed: number;
  /** How much the prop is shrunk to clear a collision (4.11); 1 is full size. */
  scale: number;
};

/** How many sprite variants one prop kind is drawn in; a prop's variant is `floor(seed) % PROP_VARIANTS`. */
export const PROP_VARIANTS = 6;

/** The quantized pose a prop is drawn in (`paint.ts` caches one sprite per pose), so the collision
 *  footprint below is the drawn shape's, not the raw plan's. */
export function propPose(d: PropDraw): { r: number; rot: number; variant: number } {
  return { r: Math.round(d.r * 2) / 2, rot: Math.round(d.rot / (Math.PI / 8)) * (Math.PI / 8), variant: Math.floor(d.seed) % PROP_VARIANTS };
}

type Circle = { x: number; y: number; r: number };

/** One shipping container of a cargo-stacks prop, relative to the prop's centre. */
export type ContainerBox = { dx: number; dy: number; len: number; wid: number; rot: number };

/**
 * The containers a cargo-stacks prop draws (ruling 9: one big container per cell, clustered): one
 * across a pair of cells, two side by side over a 2×2, one per single cell. `paint.ts` draws these
 * and {@link propFootprint} collides them, so the two can't disagree.
 */
export function containerBoxes(cluster: PropDraw["cluster"], pose: { rot: number; variant: number }): ContainerBox[] {
  if (cluster === "slab") {
    const rot = rnd(pose.variant, 1) < 0.5 ? 0 : Math.PI / 2;
    const off = CELL * 0.42;
    return [-1, 1].map((k) => ({ dx: -Math.sin(rot) * off * k, dy: Math.cos(rot) * off * k, len: CELL * 1.7, wid: CELL * 0.62, rot }));
  }
  // Containers sit square to the deck: a pair along its pair's axis, a single either way.
  const square = Math.round(pose.rot / (Math.PI / 2)) * (Math.PI / 2);
  if (cluster === "pair") return [{ dx: 0, dy: 0, len: CELL * 1.7, wid: CELL * 0.6, rot: square }];
  return [{ dx: 0, dy: 0, len: CELL * 0.78, wid: CELL * 0.52, rot: square }];
}

/** A capsule (a stroke of width `2w`, `len` end to end) as the circles along its axis. */
function capsule(c: { x: number; y: number; rot: number; len: number; w: number }): Circle[] {
  const n = Math.max(1, Math.ceil(c.len / c.w));
  const out: Circle[] = [];
  for (let k = 0; k <= n; k++) {
    const t = (k / n - 0.5) * c.len;
    out.push({ x: c.x + Math.cos(c.rot) * t, y: c.y + Math.sin(c.rot) * t, r: c.w });
  }
  return out;
}

/**
 * A prop's footprint at `scale`, as circles in board px — the solid body `paint.ts` draws, not its
 * shadow or its thinnest stubs. Mirrors the drawers there: `logs`, `trunk`, `plate` and the boulder
 * blob; small props are one circle.
 */
export function propFootprint(d: PropDraw, scale: number): Circle[] {
  const { r, rot, variant } = propPose(d);
  const at = (c: Circle[]) => c.map((q) => ({ x: d.cx + (q.x - d.cx) * scale, y: d.cy + (q.y - d.cy) * scale, r: q.r * scale }));
  const x = d.cx;
  const y = d.cy;
  switch (d.kind) {
    case "log": {
      if (d.cluster === "slab") {
        const a = rnd(variant, 1) * Math.PI;
        return at([...capsule({ x, y, rot: a, len: CELL * 1.7, w: 8.5 }), ...capsule({ x, y, rot: a + 1.1 + rnd(variant, 2) * 0.6, len: CELL * 1.5, w: 7.5 })]);
      }
      if (d.cluster === "pair") return at(capsule({ x, y, rot, len: CELL * 1.75, w: r * 0.4 }));
      return at(capsule({ x, y, rot, len: CELL * 0.95, w: r * 0.45 }));
    }
    case "trunk":
      return at([{ x, y, r: r * 1.1 }]);
    case "container":
      return at(containerBoxes(d.cluster, { rot, variant }).flatMap((b) => capsule({ x: x + b.dx, y: y + b.dy, rot: b.rot, len: b.len - b.wid, w: b.wid / 2 })));
    case "crate":
      return at([{ x, y, r: r * 1.3 }]);
    case "boulder":
      return at(d.cluster === "pair" ? capsule({ x, y, rot, len: r * 0.84, w: r * 0.6 }) : [{ x, y, r: r * 0.95 }]);
    case "hull-plate": {
      const size = d.cluster === "slab" ? 22 : d.cluster === "pair" ? 16 : r * 0.85;
      const long = d.cluster === "slab" ? 1.4 : d.cluster === "pair" ? 1.9 : 1;
      const w = size * (1.7 + 0.4 * rnd(variant, 1)) * long;
      const h = size * (0.95 + 0.35 * rnd(variant, 2));
      const a = (d.cluster === "pair" ? rot : 0) + (rnd(variant, 3) - 0.5) * (long > 1 ? 0.5 : 1.6);
      return at(capsule({ x, y, rot: a, len: Math.max(0, w - h), w: h / 2 }));
    }
    default:
      return at([{ x, y, r: r * 0.9 }]);
  }
}

function circlesMeet(a: Circle[], b: Circle[]): boolean {
  // A pixel of slack: props may touch, not cross.
  return a.some((p) => b.some((q) => Math.hypot(p.x - q.x, p.y - q.y) < p.r + q.r - 1));
}

function circleHitsCell(c: Circle, x: number, y: number): boolean {
  const nx = Math.max(x * CELL, Math.min(c.x, (x + 1) * CELL));
  const ny = Math.max(y * CELL, Math.min(c.y, (y + 1) * CELL));
  return Math.hypot(c.x - nx, c.y - ny) < c.r - 1;
}

/** The shrink steps a hard prop tries before it gives up and keeps the smallest. */
const HARD_SCALES = [1, 0.82, 0.66] as const;

/** Cells a prop's collisions are searched over beyond the window it is asked for: the widest
 *  footprint (a slab's crossed logs) reaches under two cells from its centre. */
const PROP_REACH = 3;

/** The props every region plans in `rect`, unresolved, each with the region it belongs to. */
function plannedProps(scene: PreparedScene, rect: CellRect): { d: PropDraw; region: SceneRegion }[] {
  const out: { d: PropDraw; region: SceneRegion }[] = [];
  for (const region of scene.regions) {
    const cover = region.layers.cover;
    if (!cover) continue;
    if (cover.grade === "soft") {
      for (const p of softProps(region.mask, rect, { perCell: cover.perCell, salt: region.seed % 100000 })) {
        out.push({ region, d: { kind: cover.prop, material: cover.material, shadow: cover.shadow, cluster: "single", cx: p.cx, cy: p.cy, r: p.r, rot: (p.seed % 628) / 100, seed: p.seed, scale: 1 } });
      }
    } else {
      for (const p of clusterPlan(region.mask, rect, { salt: region.seed % 100000, clustered: cover.clustered })) {
        out.push({ region, d: { kind: cover.prop, material: cover.material, shadow: cover.shadow, cluster: p.kind, cx: p.cx, cy: p.cy, r: p.r, rot: p.rot, seed: p.seed, scale: 1 } });
      }
    }
  }
  return out;
}

/** Bigger hard props claim space first, then by position; `seed` breaks ties. */
const CLUSTER_RANK = { slab: 0, pair: 1, single: 2 } as const;
function claimsBefore(a: PropDraw, b: PropDraw): boolean {
  return (CLUSTER_RANK[a.cluster] - CLUSTER_RANK[b.cluster] || a.cy - b.cy || a.cx - b.cx || a.seed - b.seed) < 0;
}

/**
 * The cover props of every region whose cells lie in `rect`, top to bottom, placed so they don't
 * collide (4.11, developer 2026-09-25): no prop crosses a raised or sunken tile, a hard prop stays
 * on its own tile's cells, and no hard prop crosses another. A hard prop that would collide shrinks through {@link HARD_SCALES} and keeps the smallest
 * (it is the tile's cover mark, so it stays); a soft prop that would touch a hard prop is dropped
 * (there are several per cell). Soft props may overlap each other inside their cell — that is the
 * look of a shrub bed.
 *
 * Each decision compares against the other props' PLANNED footprints, never their resolved ones, so
 * it depends only on props within {@link PROP_REACH} cells. A chunk therefore decides exactly what
 * the whole board would, and a terrain edit reaches only a bounded distance (`chunks.ts`'s
 * `DIRTY_REACH`).
 */
export function coverProps(scene: PreparedScene, rect: CellRect): PropDraw[] {
  const near = plannedProps(scene, { x0: rect.x0 - PROP_REACH, y0: rect.y0 - PROP_REACH, x1: rect.x1 + PROP_REACH, y1: rect.y1 + PROP_REACH });
  const hard = near.filter(({ d }) => d.shadow === "long").map(({ d }) => ({ d, body: propFootprint(d, 1) }));
  const blocked = scene.regions.filter((r) => r.layers.impassable);
  /** Does `body` cross a cell `bad` says it mustn't? */
  const crosses = (body: Circle[], bad: (x: number, y: number) => boolean) =>
    body.some((c) => {
      for (let y = Math.floor((c.y - c.r) / CELL); y <= Math.floor((c.y + c.r) / CELL); y++) {
        for (let x = Math.floor((c.x - c.r) / CELL); x <= Math.floor((c.x + c.r) / CELL); x++) {
          if (bad(x, y) && circleHitsCell(c, x, y)) return true;
        }
      }
      return false;
    });
  const isBlocked = (x: number, y: number) => blocked.some((r) => maskHas(r.mask, x, y));
  const out: PropDraw[] = [];
  for (const { d, region } of plannedProps(scene, rect)) {
    const body = propFootprint(d, 1);
    if (d.shadow === "long") {
      const rivals = hard.filter((h) => claimsBefore(h.d, d)).map((h) => h.body);
      // A hard prop stays on its own tile's cells, so it never spills past its patch.
      const foreign = (x: number, y: number) => !maskHas(region.mask, x, y);
      const fits = (scale: number) => {
        const b = scale === 1 ? body : propFootprint(d, scale);
        return !crosses(b, foreign) && !rivals.some((o) => circlesMeet(b, o));
      };
      const scale = HARD_SCALES.find(fits) ?? HARD_SCALES[HARD_SCALES.length - 1]!;
      out.push({ ...d, scale });
    } else if (!crosses(body, isBlocked) && !hard.some((h) => circlesMeet(body, h.body))) {
      out.push(d);
    }
  }
  return out.sort((a, b) => a.cy - b.cy || a.cx - b.cx || a.seed - b.seed);
}

/**
 * A raised region's wall height in px: one height for the whole region, picked from three steps in
 * the tile's range by the region's seed. A run of raised cells then reads as one mass — one lifted
 * cap, a face only on the run's outer edge (4.9, developer 2026-09-25) — and a chunk needs nothing
 * beyond the region's key.
 */
export function wallHeight({ range, seed }: { range: readonly [number, number]; seed: number }): number {
  const [lo, hi] = range;
  if (lo === hi) return lo;
  return lo + ((hi - lo) * Math.floor(rnd(seed, 7, 3) * 3)) / 2;
}
